import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  position: relative;
  min-height: 600px;
  background: #1e293b;
  color: white;
  overflow: hidden;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(56, 189, 248, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(56, 189, 248, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
  text-align: center;
`;

const SpeedIndicator = styled(motion.div)`
  font-size: 7rem;
  font-weight: 800;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  span {
    font-size: 3rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.7;
`;

const MetricsSection = styled.section`
  padding: 6rem 2rem;
  background: white;
`;

const MetricsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(56, 189, 248, 0.1);
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #38bdf8, #818cf8);
  }
`;

const MetricValue = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
`;

const ProcessSection = styled.section`
  padding: 6rem 2rem;
  background: #f8fafc;
`;

const ProcessTimeline = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(to bottom, #38bdf8, #818cf8);
    transform: translateX(-50%);
  }
`;

const ProcessStep = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  position: relative;

  &:nth-child(even) {
    direction: rtl;
    text-align: right;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    direction: ltr;
    text-align: left;
  }
`;

const ProcessContent = styled.div`
  direction: ltr;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(56, 189, 248, 0.1);
`;

const ProcessNumber = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const ProcessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const ProcessDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
`;

const BenefitsSection = styled.section`
  padding: 6rem 2rem;
  background: white;
`;

const BenefitsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  display: flex;
  gap: 2rem;
`;

const BenefitIcon = styled.div`
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.1) 0%, rgba(129, 140, 248, 0.1) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
`;

const BenefitContent = styled.div``;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const BenefitDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
`;

const CTASection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(56, 189, 248, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(56, 189, 248, 0.1) 0%, transparent 50%);
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 2rem;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  color: white;
  padding: 1.2rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  }
`;

// Add new styled components for section headers
const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
  
  span {
    background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &.light {
    color: white;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;

  &.light {
    color: #94a3b8;
  }
`;

// Add new styled component for section CTA buttons
const SectionCTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 3rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  }
`;

// Add styled components for social proof section
const SocialProofSection = styled.section`
  padding: 8rem 2rem;
  background: #f8fafc;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(56, 189, 248, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(56, 189, 248, 0.1) 0%, transparent 50%);
  }
`;

const TestimonialsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto 4rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(56, 189, 248, 0.1);
`;

const TestimonialContent = styled.p`
  color: #1e293b;
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.div`
  color: #64748b;
  font-size: 0.875rem;
`;

const CompanyLogos = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 4rem;
`;

const CompanyLogo = styled(motion.div)`
  color: #94a3b8;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: 0.1em;
`;

// Add new styled components for enhanced hero section
const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  max-width: 900px;
  margin: 4rem auto 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const HeroStat = styled(motion.div)`
  color: #94a3b8;
  text-align: center;

  strong {
    display: block;
    color: white;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #38bdf8 0%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const FeatureHighlight = styled.section`
  padding: 8rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(56, 189, 248, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(56, 189, 248, 0.05) 0%, transparent 50%);
  }
`;

const FeatureGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(56, 189, 248, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.1) 0%, rgba(129, 140, 248, 0.1) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    background: linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(129, 140, 248, 0.2) 100%);
  }
`;

function PerformanceTesting() {
  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Performance Testing', path: '/services/performance' }
      ]} />
      <Hero>
        <HeroContent>
          <SpeedIndicator
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            2.1<span>s</span>
          </SpeedIndicator>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Supercharge Your Store's Performance
          </Title>
          <Subtitle>
            Comprehensive performance testing and optimization to ensure your store delivers 
            lightning-fast experiences and handles peak traffic with confidence.
          </Subtitle>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Free Performance Audit
          </CTAButton>
          <HeroStats>
            {[
              { value: '60%', label: 'Average Speed Improvement' },
              { value: '99.9%', label: 'Uptime Guarantee' },
              { value: '24/7', label: 'Performance Monitoring' }
            ].map((stat, index) => (
              <HeroStat
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              >
                <strong>{stat.value}</strong>
                {stat.label}
              </HeroStat>
            ))}
          </HeroStats>
        </HeroContent>
      </Hero>

      <FeatureHighlight>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Comprehensive <span>Performance Solutions</span>
          </SectionTitle>
          <SectionSubtitle>
            Everything you need to ensure your store performs at its peak, even during high-traffic events
          </SectionSubtitle>
        </SectionHeader>
        <FeatureGrid>
          {[
            {
              icon: '⚡',
              title: 'Speed Optimization',
              description: 'Identify and fix performance bottlenecks to achieve lightning-fast load times.'
            },
            {
              icon: '📊',
              title: 'Load Testing',
              description: 'Simulate high traffic scenarios to ensure your store can handle peak periods.'
            },
            {
              icon: '🔍',
              title: 'Performance Monitoring',
              description: 'Real-time monitoring and alerts to maintain optimal performance.'
            },
            {
              icon: '📱',
              title: 'Mobile Performance',
              description: 'Ensure fast, responsive experiences across all mobile devices.'
            },
            {
              icon: '🌐',
              title: 'Global Performance',
              description: 'Test and optimize performance across different geographic regions.'
            },
            {
              icon: '🔄',
              title: 'Continuous Testing',
              description: 'Automated testing to maintain peak performance over time.'
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <BenefitTitle>{feature.title}</BenefitTitle>
              <BenefitDescription>{feature.description}</BenefitDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Explore All Features
          </SectionCTAButton>
        </div>
      </FeatureHighlight>

      <MetricsSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Performance <span>Metrics</span>
          </SectionTitle>
          <SectionSubtitle>
            Real-world results that demonstrate our commitment to excellence in performance optimization
          </SectionSubtitle>
        </SectionHeader>
        <MetricsGrid>
          {[
            { value: '300%', label: 'Faster Load Times' },
            { value: '99.9%', label: 'Uptime Guaranteed' },
            { value: '10x', label: 'Traffic Handling' }
          ].map((metric, index) => (
            <MetricCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <MetricValue>{metric.value}</MetricValue>
              <MetricLabel>{metric.label}</MetricLabel>
            </MetricCard>
          ))}
        </MetricsGrid>
        <div style={{ textAlign: 'center' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View Detailed Metrics
          </SectionCTAButton>
        </div>
      </MetricsSection>

      <ProcessSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Our Proven <span>Testing Process</span>
          </SectionTitle>
          <SectionSubtitle>
            A systematic, data-driven approach to identify and resolve performance bottlenecks
          </SectionSubtitle>
        </SectionHeader>
        <ProcessTimeline>
          {[
            {
              number: '01',
              title: 'Performance Audit',
              description: 'Comprehensive analysis of your current performance metrics and bottlenecks.'
            },
            {
              number: '02',
              title: 'Load Testing',
              description: 'Rigorous testing under various traffic conditions to ensure scalability.'
            },
            {
              number: '03',
              title: 'Optimization',
              description: 'Implementation of performance improvements based on test results.'
            },
            {
              number: '04',
              title: '24/7 Monitoring',
              description: 'Continuous monitoring and alerts to maintain optimal performance.'
            }
          ].map((step, index) => (
            <ProcessStep
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <ProcessContent>
                <ProcessNumber>{step.number}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessContent>
            </ProcessStep>
          ))}
        </ProcessTimeline>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule a Demo
          </SectionCTAButton>
        </div>
      </ProcessSection>

      <BenefitsSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Key <span>Benefits</span>
          </SectionTitle>
          <SectionSubtitle>
            Discover how our performance testing solutions can transform your store's speed and user experience
          </SectionSubtitle>
        </SectionHeader>
        <BenefitsGrid>
          {[
            {
              icon: '⚡',
              title: 'Faster Page Loads',
              description: 'Optimize your store\'s loading speed for better user experience.'
            },
            {
              icon: '📈',
              title: 'Higher Conversion',
              description: 'Fast stores convert better. It\'s that simple.'
            },
            {
              icon: '🔄',
              title: 'Better SEO',
              description: 'Speed is a crucial factor in search engine rankings.'
            },
            {
              icon: '💪',
              title: 'Scale Confidently',
              description: 'Handle traffic spikes during peak sales periods.'
            }
          ].map((benefit, index) => (
            <BenefitCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <BenefitIcon>{benefit.icon}</BenefitIcon>
              <BenefitContent>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitContent>
            </BenefitCard>
          ))}
        </BenefitsGrid>
        <div style={{ textAlign: 'center' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Explore All Benefits
          </SectionCTAButton>
        </div>
      </BenefitsSection>

      <SocialProofSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Trusted by <span>Industry Leaders</span>
          </SectionTitle>
          <SectionSubtitle>
            Join thousands of high-performance stores that rely on our testing solutions
          </SectionSubtitle>
        </SectionHeader>
        <TestimonialsGrid>
          {[
            {
              content: "After implementing their performance testing solutions, our average page load time decreased by 60% and our conversion rate improved by 35%. The ROI has been exceptional.",
              author: "David Zhang",
              role: "CTO at FastCommerce",
              initials: "DZ"
            },
            {
              content: "The real-time monitoring and instant alerts have helped us maintain 99.9% uptime during peak sales events. Their platform is a game-changer for our business.",
              author: "Emily Parker",
              role: "Head of Engineering at ShopElite",
              initials: "EP"
            }
          ].map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <TestimonialContent>"{testimonial.content}"</TestimonialContent>
              <TestimonialAuthor>
                <AuthorAvatar>{testimonial.initials}</AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
        <CompanyLogos>
          {[
            "SHOPIFY+",
            "AMAZON",
            "WALMART",
            "ETSY",
            "EBAY",
            "BIGCOMMERCE"
          ].map((company, index) => (
            <CompanyLogo
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.7 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {company}
            </CompanyLogo>
          ))}
        </CompanyLogos>
      </SocialProofSection>

      <CTASection>
        <CTAContent>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="light"
          >
            Ready to <span>Supercharge</span> Your Store?
          </SectionTitle>
          <SectionSubtitle className="light">
            Join thousands of high-performance stores and start optimizing your performance today
          </SectionSubtitle>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started Free
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default PerformanceTesting;